import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Los avisos flotantes — **toda acción que escribe necesita uno**.
 *
 * Es la regla del estándar de estilos, y su motivo es el que no se ve: cuando
 * apagar un extra, quitarlo de doce productos y borrar un grupo se hacen todos
 * «al momento», **el éxito no tiene forma**. La fila se queda igual o desaparece,
 * y quien lo hizo no sabe si el servidor contestó, si contestó que no, o si lo
 * que pulsó era lo que creía. Un acuse de dos líneas es la diferencia entre una
 * pantalla que responde y una que sólo cambia sola.
 *
 * ── Fondo claro, icono a color. Nunca al revés ────────────────────────────
 * Cuatro variantes con degradado muy claro, icono a color dentro de un círculo
 * blanco y filete de 1 px del mismo tono. **Nunca un bloque de color saturado
 * con texto blanco encima**: eso es un cartel, y un cartel se lee como una
 * interrupción — justo lo que un acuse de «listo» no debe ser.
 *
 * Los cuatro tonos son los fijos de `ADR-019` —`--fr-vivo`, `--fr-peligro`,
 * `--fr-aviso`, `--fr-info`— y no los literales del estándar. Dos razones, y la
 * segunda es dura: significan lo mismo en las nueve paletas (un verde que
 * significa «hecho» tiene que significarlo en la cafetería de paleta verde), y
 * el carril `verificar-colores` rechaza cualquier literal fuera de `tokens.css`.
 *
 * ── El título dice el RESULTADO, la descripción dice QUÉ PASÓ ──────────────
 * «Listo» / «Sumamos tu sello #7». Separarlo así es lo que permite leer sólo la
 * primera línea cuando ya sabes lo que hiciste, y la segunda cuando no.
 *
 * ── Se va solo a los 3.5 s, y tiene una ✕ ─────────────────────────────────
 * Las dos cosas. Sin el plazo se apilan; sin la ✕ hay que esperarlo cuando tapa
 * justo lo que quieres tocar. El plazo **se detiene al pasar el ratón o al
 * enfocar con el teclado**, que no lo pide el estándar pero lo pide leerlo: 3.5
 * segundos es poco para dos líneas si además hay que decidir algo.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, } from 'react';
/** Milisegundos que un aviso vive solo. Del estándar. */
export const PLAZO_DEL_AVISO = 3_500;
const Contexto = createContext(null);
/**
 * `useAviso()` desde cualquier pantalla de debajo del proveedor.
 *
 * Fuera del proveedor **no lanza**: devuelve una función que no hace nada. Es
 * deliberado y tiene precio pagado en otra pantalla — una prueba que monta un
 * componente suelto no debería reventar por no traer el andamiaje, y una pantalla
 * que se olvide del proveedor tiene que seguir funcionando sin acuse antes que
 * caerse entera. Lo que se pierde es el acuse; lo que se salvaría lanzando es
 * nada.
 */
export function useAviso() {
    const avisar = useContext(Contexto);
    return avisar ?? sinAvisos;
}
const sinAvisos = () => { };
export function ProveedorDeAvisos({ children, plazo = PLAZO_DEL_AVISO }) {
    const [cola, setCola] = useState([]);
    const siguiente = useRef(0);
    const avisar = useCallback((nuevo) => {
        siguiente.current += 1;
        const id = siguiente.current;
        setCola((antes) => [...antes, { ...nuevo, id }]);
    }, []);
    const cerrar = useCallback((id) => {
        setCola((antes) => antes.filter((a) => a.id !== id));
    }, []);
    return (_jsxs(Contexto.Provider, { value: avisar, children: [children, _jsx("div", { className: "fr-avisos", "aria-live": "polite", "aria-atomic": "false", children: cola.map((aviso) => (_jsx(FichaDeAviso, { aviso: aviso, plazo: plazo, alCerrar: () => { cerrar(aviso.id); } }, aviso.id))) })] }));
}
const ICONOS = {
    exito: _jsx("path", { d: "M3.5 8.5l3 3 6-6.5" }),
    error: _jsxs(_Fragment, { children: [_jsx("circle", { cx: "8", cy: "8", r: "6" }), _jsx("path", { d: "M8 5v4M8 11h.01" })] }),
    atencion: _jsxs(_Fragment, { children: [_jsx("path", { d: "M8 2.5l6 11H2z" }), _jsx("path", { d: "M8 7v3M8 11.8h.01" })] }),
    info: _jsxs(_Fragment, { children: [_jsx("circle", { cx: "8", cy: "8", r: "6" }), _jsx("path", { d: "M8 7.2v4M8 5h.01" })] }),
};
function FichaDeAviso({ aviso, plazo, alCerrar }) {
    const [detenido, setDetenido] = useState(false);
    useEffect(() => {
        if (detenido)
            return;
        const reloj = setTimeout(alCerrar, plazo);
        return () => { clearTimeout(reloj); };
    }, [detenido, plazo, alCerrar]);
    return (_jsxs("div", { className: `fr-aviso-flotante fr-aviso-flotante--${aviso.tono}`, 
        // Detener el plazo al posarse encima. La mitad de teclado va en la ✕, que
        // es lo único enfocable que hay aquí dentro — ponerla en este `div` la
        // dejaba colgada de que `focusin` burbujee, que es una cadena más larga
        // para el mismo efecto y que no se puede medir en jsdom.
        onMouseEnter: () => { setDetenido(true); }, onMouseLeave: () => { setDetenido(false); }, children: [_jsx("span", { className: "fr-aviso-flotante__icono", "aria-hidden": "true", children: _jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", strokeLinejoin: "round", children: ICONOS[aviso.tono] }) }), _jsxs("span", { className: "fr-aviso-flotante__texto", children: [_jsx("strong", { className: "fr-aviso-flotante__titulo", children: aviso.titulo }), _jsx("span", { className: "fr-aviso-flotante__detalle", children: aviso.detalle })] }), _jsx("button", { type: "button", className: "fr-aviso-flotante__cerrar", "aria-label": `Cerrar el aviso «${aviso.titulo}»`, 
                // Quien llega hasta aquí con el tabulador no tiene ratón que posar, y
                // sin esto el aviso se le va de debajo del dedo justo cuando iba a
                // cerrarlo — llevándose además el foco al cuerpo del documento.
                onFocus: () => { setDetenido(true); }, onBlur: () => { setDetenido(false); }, onClick: alCerrar, children: _jsx("svg", { width: "14", height: "14", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", "aria-hidden": "true", children: _jsx("path", { d: "M4.5 4.5l7 7M11.5 4.5l-7 7" }) }) })] }));
}
//# sourceMappingURL=avisos.js.map